import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Vote, ShieldCheck, Hash, Lock } from "lucide-react";
import CandidateCard from "@/components/CandidateCard";
import ResultsView from "@/components/ResultsView";
import StampOverlay from "@/components/StampOverlay";
import ShareCard from "@/components/ShareCard";
import LangToggle from "@/components/LangToggle";
import TurnstileWidget from "@/components/TurnstileWidget";
import StatsBanner from "@/components/StatsBanner";
import FrozenAnnouncement from "@/components/FrozenAnnouncement";
import PublicPulseVsOfficial from "@/components/PublicPulseVsOfficial";
import FinalTimeline from "@/components/FinalTimeline";
import VelocitySparkline from "@/components/VelocitySparkline";
import CivicThanks from "@/components/CivicThanks";
import WaitlistForm from "@/components/WaitlistForm";
import SeoHead from "@/components/SeoHead";
import { getOrCreateVoterId, getLocalVote, setLocalVote } from "@/lib/voter";
import { useLang } from "@/lib/useLang";
import Analytics from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ArchivedKeralaCM() {
  const { t, lang } = useLang();
  const [candidates, setCandidates] = useState([]);
  const [voterId] = useState(getOrCreateVoterId);
  const [view, setView] = useState("loading"); // loading | voting | results
  const [votedFor, setVotedFor] = useState(null);
  const [casting, setCasting] = useState(null);
  const [results, setResults] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showStamp, setShowStamp] = useState(false);
  const [error, setError] = useState(null);
  const [pollState, setPollState] = useState(null);
  const turnstileRef = useRef(null);

  const frozen = !!pollState?.frozen;

  const loadResults = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/results`);
      setResults(r.data);
    } catch (e) {
      console.error("results error", e);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Bootstrap: fetch poll-state + candidates + check vote status
  useEffect(() => {
    (async () => {
      try {
        const [stateRes, candRes, voteRes] = await Promise.all([
          axios.get(`${API}/poll-state`).catch(() => ({ data: { frozen: false } })),
          axios.get(`${API}/candidates`),
          axios.get(`${API}/has-voted/${voterId}`),
        ]);
        setPollState(stateRes.data);
        setCandidates(candRes.data.candidates);

        const localVote = getLocalVote();
        const serverVoted = voteRes.data.voted;

        // Frozen poll: always show final results; still preserve the user's vote tag if any
        if (stateRes.data?.frozen) {
          const cId = voteRes.data.candidate_id || localVote;
          if (cId) {
            setVotedFor(cId);
            setLocalVote(cId);
          }
          await loadResults();
          setView("results");
          return;
        }

        if (serverVoted || localVote) {
          const cId = voteRes.data.candidate_id || localVote;
          setVotedFor(cId);
          if (cId) setLocalVote(cId);
          await loadResults();
          setView("results");
        } else {
          setView("voting");
        }
      } catch (e) {
        console.error("bootstrap error", e);
        setError(t("error_server"));
        setView("voting");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voterId, loadResults]);

  const handleVote = async (candidate) => {
    if (frozen) return; // hard block on the client too
    setCasting(candidate.id);
    setError(null);
    try {
      // Grab a fresh Turnstile token (best-effort — backend bypasses if not configured)
      let token = null;
      try {
        token = await turnstileRef.current?.getToken?.();
      } catch (e) { /* ignore — backend will reject if required */ }

      await axios.post(`${API}/vote`, {
        voter_id: voterId,
        candidate_id: candidate.id,
        turnstile_token: token || undefined,
      });
      Analytics.voteCast(candidate.id);
      setLocalVote(candidate.id);
      setVotedFor(candidate.id);
      setShowStamp(true);
      await loadResults();
      setTimeout(() => {
        setShowStamp(false);
        setView("results");
        Analytics.resultsView(results?.total_votes || 0);
      }, 1100);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) {
        // Server flipped to frozen between bootstrap and click — refresh state.
        try {
          const s = await axios.get(`${API}/poll-state`);
          setPollState(s.data);
          await loadResults();
          setView("results");
        } catch (e2) {
          console.warn("410 sync failed", e2);
        }
      } else if (status === 409) {
        try {
          const r = await axios.get(`${API}/has-voted/${voterId}`);
          if (r.data.candidate_id) {
            setVotedFor(r.data.candidate_id);
            setLocalVote(r.data.candidate_id);
          }
          await loadResults();
          setView("results");
        } catch (e2) {
          console.warn("409 sync failed", e2);
        }
      } else if (status === 429) {
        Analytics.voteBlocked(429);
        setError(e?.response?.data?.detail || "Too many vote attempts. Please try again in an hour.");
      } else if (status === 403) {
        Analytics.voteBlocked(403);
        setError(e?.response?.data?.detail || "Verification failed. Please reload and try again.");
      } else {
        setError(t("error_vote"));
      }
    } finally {
      setCasting(null);
    }
  };

  const votedCandidate = useMemo(
    () => candidates.find((c) => c.id === votedFor) || null,
    [candidates, votedFor]
  );

  // Synthetic "candidate" object for the celebration ShareCard in frozen mode.
  // If the announced CM matches one of the 3 contenders, reuse that contender's
  // color/initials. Otherwise default to saffron.
  const celebrationCandidate = useMemo(() => {
    if (!pollState?.frozen || !pollState?.announced_cm_name) return null;
    const match = candidates.find((c) => c.id === pollState.announced_cm_id);
    const name = pollState.announced_cm_name;
    const photo = pollState.announced_cm_photo || match?.photo || "";
    const initials = match?.initials || name
      .split(/\s+/)
      .map((w) => w.replace(/[^A-Za-z\u0D00-\u0D7F]/g, "")[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "CM";
    return {
      id: pollState.announced_cm_id || "announced-cm",
      name,
      initials,
      color: match?.color || "saffron",
      photo,
    };
  }, [pollState, candidates]);

  return (
    <div className="App min-h-screen">
      <SeoHead
        title={lang === "ml"
          ? "കേരളം മുഖ്യമന്ത്രി 2026 പോൾ — അന്തിമ ഫലം"
          : "Kerala CM 2026 Citizens' Poll — Final Results (Archived)"}
        description={lang === "ml"
          ? "അഞ്ച് ലക്ഷം പൗരന്മാർ വോട്ട് ചെയ്ത പോളിന്റെ അന്തിമ ഫലം. വി.ഡി. സതീശൻ പുതിയ മുഖ്യമന്ത്രി. വോട്ടെടുപ്പ് അവസാനിച്ചു."
          : "Archived final results of the citizens' poll that gathered 500,000+ votes choosing Kerala's new Chief Minister. V.D. Satheesan officially announced. Public Pulse vs Official Decision, milestones, velocity chart."}
        canonical="/previous-polls/kerala-cm-2026"
        schema={[
          {
            "@type": "Article",
            "headline": "Kerala CM 2026 Citizens' Poll — Final Results",
            "datePublished": "2026-05-11T16:00:00+05:30",
            "dateModified": "2026-05-15T14:30:00+05:30",
            "articleSection": "Civic Polls",
            "inLanguage": ["en", "ml"],
            "publisher": { "@id": "https://thenextcm.com/#org" }
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thenextcm.com/" },
              { "@type": "ListItem", "position": 2, "name": "Previous Polls", "item": "https://thenextcm.com/previous-polls" },
              { "@type": "ListItem", "position": 3, "name": "Kerala CM 2026", "item": "https://thenextcm.com/previous-polls/kerala-cm-2026" }
            ]
          }
        ]}
      />
      <StampOverlay visible={showStamp} />

      {/* Top banner — switches to frozen variant after announcement.
          Held until pollState resolves so we don't flash the live banner. */}
      {pollState !== null && <StatsBanner voted={!!votedFor} frozen={frozen} />}

      {/* HEADER */}
      <header className="border-b-4 border-black" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="border-4 border-black flex items-center justify-center shrink-0"
              style={{ background: "#0A0A0A", color: "#FF9933", width: 44, height: 44 }}
              aria-hidden="true"
            >
              <Vote size={22} strokeWidth={3} />
            </div>
            <div>
              <p className="text-[10px] md:text-xs uppercase font-bold tracking-[0.3em]">
                {t("brand_eyebrow")}
              </p>
              <p className="font-display uppercase leading-none text-lg md:text-xl">
                {t("brand_name")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 md:gap-6">
            <div className="hidden lg:flex items-center gap-6 text-xs uppercase font-bold tracking-[0.25em]">
              <span className="flex items-center gap-2" data-testid="header-trust">
                <ShieldCheck size={16} strokeWidth={3} /> {t("one_vote_browser")}
              </span>
              <span className="flex items-center gap-2" data-testid="header-id">
                <Hash size={16} strokeWidth={3} /> {voterId.slice(0, 8)}
              </span>
            </div>
            <LangToggle />
          </div>
        </div>
      </header>

      {/* HERO — gated until poll-state resolves to avoid flashing the wrong tense */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-8">
        {pollState === null ? (
          <div
            className="font-display uppercase leading-[0.85] tracking-tighter"
            style={{ fontSize: "clamp(3.25rem, 11vw, 9rem)", color: "transparent" }}
            aria-hidden="true"
          >
            KERALA
          </div>
        ) : (
          <>
            <p
              className="text-xs md:text-sm uppercase font-bold tracking-[0.4em] text-neutral-600 mb-6"
              data-testid="hero-eyebrow"
            >
              {frozen ? t("announcement_eyebrow") : t("hero_eyebrow")}
            </p>
            <h1
              className="font-display uppercase leading-[0.85] tracking-tighter"
              style={{ fontSize: "clamp(3.25rem, 11vw, 9rem)" }}
              data-testid="hero-title"
            >
              {frozen ? t("hero_title_decided_1") : t("hero_title_1")}<br />
              <span style={{ color: "#E3000F" }}>
                {frozen ? t("hero_title_decided_2") : t("hero_title_2")}
              </span>
            </h1>

            {/* Live-poll callouts hidden in frozen mode (replaced by hero_sub_frozen) */}
            {!frozen && (
              <div className="mt-8 max-w-3xl space-y-2" data-testid="hero-callout">
                <p
                  className="font-display uppercase leading-tight"
                  style={{ fontSize: "clamp(1.25rem, 2.6vw, 2rem)" }}
                  data-testid="hero-callout-million"
                >
                  {t("hero_callout_million")}
                </p>
                <p
                  className="text-base md:text-lg font-bold"
                  style={{ color: "#404040" }}
                  data-testid="hero-callout-tag"
                >
                  {t("hero_callout_tag")}
                </p>
              </div>
            )}

            <p
              className="mt-6 max-w-2xl text-lg md:text-xl font-medium leading-relaxed"
              data-testid="hero-sub"
            >
              {frozen
                ? t("hero_sub_frozen")
                : view === "results"
                  ? t("hero_sub_results")
                  : t("hero_sub_vote")}
            </p>
          </>
        )}
      </section>

      {/* MAIN */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        {error && (
          <div
            className="brutal-card p-4 mb-8"
            style={{ background: "#FFE4E6", borderColor: "#E3000F" }}
            data-testid="error-banner"
          >
            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: "#E3000F" }}>
              {error}
            </p>
          </div>
        )}

        {view === "loading" && !frozen && (
          <p className="font-display uppercase text-2xl" data-testid="loading-state">
            {t("loading")}
          </p>
        )}

        {/* ---------- FROZEN STATE ---------- */}
        {frozen && (
          <div className="space-y-12 md:space-y-16" data-testid="frozen-state">
            <FrozenAnnouncement pollState={pollState} />

            <PublicPulseVsOfficial results={results} pollState={pollState} />

            <FinalTimeline pollState={pollState} results={results} />

            <VelocitySparkline />

            <CivicThanks />

            <WaitlistForm />

            {/* Disabled ballot with "Voting closed" overlay — for visitors who never voted */}
            {!votedFor && candidates.length > 0 && (
              <section
                className="relative space-y-6"
                data-testid="disabled-ballot-section"
              >
                <h2
                  className="font-display uppercase leading-none"
                  style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.25rem)" }}
                >
                  {t("ballot_heading")}
                </h2>
                <div className="relative">
                  <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12 pointer-events-none select-none"
                    style={{ filter: "grayscale(0.9)", opacity: 0.45 }}
                    aria-hidden="true"
                  >
                    {candidates.map((c, i) => (
                      <CandidateCard
                        key={c.id}
                        candidate={c}
                        index={i}
                        disabled
                        onVote={() => {}}
                      />
                    ))}
                  </div>

                  {/* Overlay */}
                  <div
                    className="absolute inset-0 flex items-center justify-center p-6"
                    data-testid="voting-closed-overlay"
                  >
                    <div
                      className="brutal-card p-6 md:p-8 max-w-md text-center"
                      style={{ background: "#0A0A0A", color: "#FDFBF7" }}
                    >
                      <div
                        className="border-4 border-black flex items-center justify-center mx-auto mb-4"
                        style={{ background: "#FF9933", color: "#0A0A0A", width: 64, height: 64 }}
                        aria-hidden="true"
                      >
                        <Lock size={28} strokeWidth={3} />
                      </div>
                      <p
                        className="font-display uppercase leading-none"
                        style={{ fontSize: "clamp(1.5rem, 4vw, 2.25rem)", color: "#FF9933" }}
                        data-testid="voting-closed-headline"
                      >
                        {t("voting_closed_overlay")}
                      </p>
                      <p
                        className="text-sm md:text-base font-bold uppercase tracking-[0.25em] mt-3"
                        style={{ color: "#FDFBF7" }}
                      >
                        {t("voting_closed_sub")}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Celebration share card — always shown in frozen mode, voted or not */}
            {celebrationCandidate && (
              <ShareCard
                candidate={celebrationCandidate}
                allCandidates={candidates}
                pollState={pollState}
              />
            )}
          </div>
        )}

        {/* ---------- LIVE VOTING ---------- */}
        {!frozen && view === "voting" && (
          <>
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
              <h2
                className="font-display uppercase leading-none"
                style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
                data-testid="ballot-heading"
              >
                {t("ballot_heading")}
              </h2>
              <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
                {t("ballot_help")}
              </p>
            </div>

            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12"
              data-testid="candidates-grid"
            >
              {candidates.map((c, i) => (
                <CandidateCard
                  key={c.id}
                  candidate={c}
                  index={i}
                  disabled={casting !== null}
                  onVote={handleVote}
                />
              ))}
            </div>
          </>
        )}

        {/* ---------- LIVE RESULTS ---------- */}
        {!frozen && view === "results" && (
          <div className="space-y-12">
            <ResultsView
              data={results}
              votedFor={votedFor}
              onRefresh={loadResults}
              refreshing={refreshing}
            />
            {votedCandidate && (
              <ShareCard
                candidate={votedCandidate}
                allCandidates={candidates}
                pollState={pollState}
              />
            )}
          </div>
        )}
      </main>

      {/* Cloudflare Turnstile — invisible/managed mode, only on the live voting view */}
      {!frozen && view === "voting" && (
        <div
          style={{ position: "fixed", bottom: 16, right: 16, zIndex: 40, opacity: 0.92 }}
          data-testid="turnstile-mount"
        >
          <TurnstileWidget ref={turnstileRef} />
        </div>
      )}

      {/* FOOTER */}
      <footer className="border-t-4 border-black" data-testid="app-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-xs uppercase font-bold tracking-[0.3em]">
            {t("footer_left")}
          </p>
          <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
            {t("footer_right")}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default ArchivedKeralaCM;
